/**
 * Error labels — display-only mappings for backend error codes.
 *
 * Each backend exception has a stable error_code. The user-facing
 * message in the management console should NOT show the raw code.
 * This module turns a code into a friendly Chinese sentence.
 *
 * If the code is unknown we keep the message generic — never leak
 * the raw code or the full stack trace to the operator. The console
 * shows a brief sentence; the diagnostic detail is logged to the
 * server / browser console for the operator to copy if they need
 * to escalate.
 */

export interface FriendlyError {
  /** Short, user-facing headline — what went wrong. */
  headline: string;
  /** What the operator can try. Optional. */
  hint?: string;
  /** True if the operator should refresh and try again. */
  reloadable?: boolean;
}

const TABLE: Record<string, FriendlyError> = {
  // ---- auth ----
  AUTH_INVALID_CREDENTIALS: {headline: '邮箱或密码错误', hint: '请检查后重试。'},
  AUTH_TOKEN_EXPIRED: {headline: '登录已过期，请重新登录', reloadable: true},
  AUTH_TOKEN_INVALID: {headline: '登录状态无效，请重新登录', reloadable: true},
  AUTH_FORBIDDEN: {headline: '没有操作权限'},

  // ---- editor / revision conflict ----
  EDITOR_REVISION_CONFLICT: {
    headline: '页面已被其他操作更新，请刷新后重试。',
    hint: '这是协作场景下的正常情况。刷新后会自动加载最新版本。',
    reloadable: true,
  },

  // ---- image service ----
  IMAGE_SERVICE_UNREACHABLE: {
    headline: '图片服务暂时不可用，请稍后重试。',
    hint: '稍等几秒后再次点击生成即可。',
  },
  IMAGE_SERVICE_BAD_RESPONSE: {
    headline: '图片服务返回异常，请重新生成。',
  },
  IMAGE_SERVICE_PROVIDER_ERROR: {
    headline: '图片生成失败，请稍后重试。',
    hint: '换一个描述或换一张候选图试试。',
  },
  IMAGE_SERVICE_QUOTA_EXCEEDED: {
    headline: '图片生成次数已达上限。',
    hint: '请联系管理员提升配额。',
  },

  // ---- AI generation ----
  AI_ROUTER_UNREACHABLE: {
    headline: 'AI 服务暂时不可用，请稍后重试。',
    hint: '你可以继续手动编辑当前区块。',
  },
  AI_ROUTER_BAD_SCHEMA: {
    headline: 'AI 服务返回格式异常，请重新生成。',
  },
  AI_ROUTER_NOT_CONFIGURED: {
    headline: 'AI 服务尚未配置。',
    hint: '请联系管理员。',
  },
  AI_GENERATION_SCHEMA_INVALID: {
    headline: 'AI 生成结果格式异常，请重新生成。',
  },
  AI_GENERATION_CONTENT_REJECTED: {
    headline: 'AI 生成内容被安全审核拒绝，请更换提示词后重试。',
  },
  AI_SITE_BRIEF_SAVE_FAILED: {
    headline: '资料保存失败，请检查填写内容后重试。',
  },
  AI_SITE_PLAN_MISMATCH: {
    headline: '资料版本已更新，请刷新后重试。',
    reloadable: true,
  },
  AI_SITE_TEMPLATE_NOT_READY: {
    headline: '网站模板尚未准备完成。',
    hint: '请先完成网站准备，系统会自动应用基础模板后继续。',
  },
  EDITOR_VALIDATION_FAILED: {
    headline: '提交信息有误，请检查填写内容后重试。',
  },

  // ---- server check / deploy ----
  SSH_CONNECT_FAILED: {
    headline: 'SSH 连接失败，请检查网络或凭据。',
  },
  SSH_AUTH_FAILED: {
    headline: 'SSH 认证失败，请检查用户名和密码 / 私钥。',
  },
  SSH_TIMEOUT: {
    headline: 'SSH 连接超时，请稍后重试。',
  },
  DEPLOY_STEP_FAILED: {
    headline: '部署步骤失败，请查看技术详情。',
  },
  NO_ELIGIBLE_MANAGED_NODE: {
    headline: '暂无可用托管节点，请联系管理员。',
  },
  MANAGED_NODE_NOT_ELIGIBLE: {
    headline: '当前托管节点不可用于新部署，请联系管理员。',
  },
  SITE_NOT_DEPLOYED: {
    headline: '站点尚未完成部署。',
    hint: '请先点击「开始部署」。',
  },
  SITE_AGENT_NOT_READY: {
    headline: '网站连接组件尚未准备完成。',
    hint: '请先完成网站准备。',
  },
  SITE_AGENT_NOT_CONFIGURED: {
    headline: '网站连接组件尚未准备完成。',
    hint: '请先完成网站准备。',
  },
  TEMPLATE_NOT_READY: {
    headline: '尚未应用网站模板，请先选择模板。',
    hint: '应用模板完成后才能预览或发布。',
  },
  MEDIA_NOT_CONNECTED: {
    headline: '网站媒体库尚未连接。',
    hint: '请先完成网站准备。',
  },
  SITE_DOMAIN_MISSING: {
    headline: '请先设置网站主域名。',
    hint: '返回站点设置填写主域名后再试。',
  },
  DNS_NOT_RESOLVED: {
    headline: '域名 DNS 还未解析到此服务器。',
    hint: '请到域名服务商添加 A 记录。',
  },
  CERTBOT_FAILED: {
    headline: 'SSL 证书签发失败。',
    hint: '通常与 DNS 解析或端口有关，请稍后重试。',
  },

  // ---- generic ----
  VALIDATION_ERROR: {
    headline: '提交信息有误，请检查后重试。',
  },
  NOT_FOUND: {
    headline: '未找到对应记录。',
  },
  RATE_LIMITED: {
    headline: '操作过于频繁，请稍后再试。',
  },
  INTERNAL_ERROR: {
    headline: '系统繁忙，请稍后重试。',
  },
};

/** Get a friendly error for a code; falls back to a generic message. */
export function friendlyError(code: string | null | undefined, fallbackMessage?: string): FriendlyError {
  if (code && TABLE[code]) return TABLE[code];
  return {headline: fallbackMessage || '操作失败，请稍后重试。'};
}

/** Format a full error display: headline + optional hint. */
export function formatError(code: string | null | undefined, fallbackMessage?: string): string {
  const f = friendlyError(code, fallbackMessage);
  return f.hint ? `${f.headline} ${f.hint}` : f.headline;
}
